import Image from "next/image";
import { CustomButton } from "@/components/ui/custom-button";
import { ReactNode } from "react";

/**
 * AlternatingSection Component
 * 
 * A flexible, reusable component that creates alternating left-right sections.
 * Automatically alternates layout: first section = text-left/image-right, 
 * second = image-left/text-right, third = text-left/image-right, and so on.
 * 
 * Features:
 * - Supports any number of sections (2, 3, 4, 5, etc.)
 * - Consistent image dimensions across all sections
 * - Optional button integration with CustomButton
 * - Full TypeScript support
 * - Responsive design with exact same styling as original LeftRightSection
 * 
 * @example
 * const sections = [
 *   {
 *     title: "First Section",
 *     content: "This is text content for the first section",
 *     image: { src: "/image1.jpg", alt: "First image" }
 *   },
 *   {
 *     title: "Second Section", 
 *     content: <p>Complex JSX content</p>,
 *     image: { src: "/image2.jpg", alt: "Second image" },
 *     button: { text: "Click Me", variant: "primary", onClick: () => {} }
 *   }
 * ];
 * 
 * <AlternatingSection sections={sections} />
 */

// TypeScript interfaces
interface SectionImage {
  src: string;
  alt: string;
  width?: number;
  height?: number;
}

interface SectionVideo {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  containerClassName?: string;
  type?: 'mp4' | 'gif';
}

interface SectionButton {
  text: string;
  variant?: "primary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  onClick?: () => void;
}

interface SectionData {
  title: string;
  subtitle?: string;
  content: string | ReactNode;
  image?: SectionImage;
  video?: SectionVideo;
  button?: SectionButton;
}

interface AlternatingSectionProps {
  sections: SectionData[];
  className?: string;
  defaultImageWidth?: number;
  defaultImageHeight?: number;
}

export function AlternatingSection({ 
  sections,
  className = "",
  defaultImageWidth = 600,
  defaultImageHeight = 500
}: AlternatingSectionProps) {
  const getSectionPadding = (index: number, totalSections: number) => {
    if (index === 0) return "pb-12 sm:pb-20";
    if (index === totalSections - 1) return "pt-12 sm:pt-20 pb-20 sm:pb-40";
    return "pt-12 pb-12 sm:py-20";
  };

  const isEvenIndex = (index: number) => index % 2 === 0;

  const getBackgroundColor = (index: number) => {
    const colors = ['ss-yellow', 'ss-blue', 'ss-green', 'ss-purple'];
    return colors[index % colors.length];
  };

  const renderContent = (content: string | ReactNode) => {
    if (typeof content === "string") {
      return <p className="text-sm sm:text-base">{content}</p>;
    }
    return (
      <div className="text-sm sm:text-base [&>p]:text-sm [&>p]:sm:text-base">
        {content}
      </div>
    );
  };

  return (
    <div className={className}>
      {sections.map((section, index) => {
        const isEven = isEvenIndex(index);
        const padding = getSectionPadding(index, sections.length);
        
        return (
          <section key={index} className={padding}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12 lg:gap-16 items-center">
                {/* Text Content */}
                <div className={`${isEven ? 'lg:pr-8' : 'lg:pl-8 order-1 lg:order-2'} mb-6 lg:mb-0`}>
                  {section.subtitle && (
                    <p className="mb-3 sm:mb-4 text-sm sm:text-base">
                      {section.subtitle}
                    </p>
                  )}
                  <h2 className="mb-4 sm:mb-6 text-xl sm:text-2xl font-bold">
                    {section.title}
                  </h2>
                  <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
                    {renderContent(section.content)}
                  </div>
                  {section.button && (
                    <div className="mt-6 sm:mt-8">
                      <CustomButton
                        variant={section.button.variant || "primary"}
                        size={section.button.size || "lg"}
                        onClick={section.button.onClick}
                      >
                        {section.button.text}
                      </CustomButton>
                    </div>
                  )}
                </div>

                {/* Image or Video */}
                <div className={`relative z-10 ${isEven ? '' : 'order-2 lg:order-1'} w-full max-w-[600px] mx-auto lg:mx-0 h-[250px] sm:h-[300px] md:h-[400px] lg:h-[500px]`}>
                  {section.image ? (
                    <div className="rounded-2xl sm:rounded-3xl overflow-hidden w-full h-full">
                      <Image
                        src={section.image.src}
                        alt={section.image.alt}
                        width={section.image.width || defaultImageWidth}
                        height={section.image.height || defaultImageHeight}
                        className="w-full h-full object-cover"
                        priority
                      />
                    </div>
                  ) : section.video ? (
                    <div className={`rounded-2xl sm:rounded-3xl overflow-hidden w-full h-full px-2 pt-3 sm:px-4 sm:pt-6 shadow-xl md:px-10 md:pt-10 bg-${getBackgroundColor(index)}/20`}>
                      {section.video.type === 'gif' ? (
                        <Image
                          src={section.video.src}
                          alt={section.video.alt}
                          width={section.video.width || defaultImageWidth}
                          height={section.video.height || defaultImageHeight}
                          className="w-full h-full object-cover rounded-xl"
                          priority
                        />
                      ) : (
                        <video
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="w-full h-full object-cover rounded-xl"
                          aria-label={section.video.alt}
                        >
                          <source src={section.video.src} type="video/mp4" />
                        </video>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
